import { collection, getFirestore, onSnapshot, query, where, type DocumentData } from 'firebase/firestore'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { firebaseApp } from './lib/firebase'

const contactEmail = import.meta.env.VITE_CONTACT_EMAIL || 'hello@vowcanvas.app'
const iosUrl = import.meta.env.VITE_IOS_APP_URL || 'https://apps.apple.com/app/vowcanvas/id6804843844'
const androidUrl = import.meta.env.VITE_ANDROID_APP_URL || 'https://play.google.com/store/apps/details?id=com.halyard.vowcanvas'

type MosaicProps = {
  compact?: boolean
  replayKey?: number
  className?: string
}

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <path d="M4 10h11M11 5l5 5-5 5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
    </svg>
  )
}

function MosaicAssembly({ compact = false, replayKey = 0, className = '' }: MosaicProps) {
  const columns = compact ? 8 : 10
  const count = columns * columns
  const tiles = Array.from({ length: count })

  return (
    <div
      key={replayKey}
      className={`mosaic mosaic--${compact ? 'compact' : 'full'} ${className}`}
      style={{ '--columns': columns } as CSSProperties}
      role="img"
      aria-label="Small proof photos assembling into a portrait of a father and son"
    >
      {tiles.map((_, index) => {
        const row = Math.floor(index / columns)
        const column = index % columns
        const angle = (index * 137.508 * Math.PI) / 180
        const distance = (compact ? 180 : 330) + (index % 7) * 22
        const x = Math.cos(angle) * distance
        const y = Math.sin(angle) * distance
        const backgroundX = (column / (columns - 1)) * 100
        const backgroundY = (row / (columns - 1)) * 100
        const style = {
          '--from-x': `${x}px`,
          '--from-y': `${y}px`,
          '--delay': `${(index % 19) * 32 + Math.floor(index / 19) * 18}ms`,
          '--turn': `${(index % 2 ? 1 : -1) * (16 + (index % 5) * 9)}deg`,
          backgroundPosition: `${backgroundX}% ${backgroundY}%`,
        } as CSSProperties

        return <span className="mosaic__tile" style={style} key={index} />
      })}
      <span className="mosaic__shine" aria-hidden="true" />
    </div>
  )
}

function Logo({ reversed = false }: { reversed?: boolean }) {
  return (
    <img
      className="brand-logo"
      src={reversed ? '/brand/vowcanvas-logo-reversed.svg' : '/brand/vowcanvas-logo-horizontal.svg'}
      width="195"
      height="38"
      alt="VowCanvas"
    />
  )
}

function StoreButton({ platform, url }: { platform: 'iOS' | 'Android'; url?: string }) {
  const icon = platform === 'iOS' ? '●' : '▶'
  const label = url ? `Download on ${platform}` : `Coming to ${platform}`

  if (url) {
    return (
      <a className="store-button" href={url} target="_blank" rel="noreferrer">
        <span className="store-button__icon" aria-hidden="true">{icon}</span>
        <span><small>{platform === 'Android' ? 'Get it on' : 'Download on the'}</small>{platform === 'Android' ? 'Google Play' : 'App Store'}</span>
      </a>
    )
  }

  return (
    <span className="store-button store-button--disabled" aria-label={`${label}, not yet available`}>
      <span className="store-button__icon" aria-hidden="true">{icon}</span>
      <span><small>Coming to</small>{platform}</span>
    </span>
  )
}

type SharedVow = {
  id: string
  title: string
  image: string
  completedOccurrences: number
  totalOccurrences: number
}

function sharedVow(document: DocumentData): SharedVow | null {
  const data = document.data()
  if (
    typeof data.title !== 'string'
    || typeof data.imageURL !== 'string'
    || typeof data.completedOccurrences !== 'number'
    || typeof data.totalOccurrences !== 'number'
  ) return null

  return {
    id: document.id,
    title: data.title,
    image: data.imageURL,
    completedOccurrences: data.completedOccurrences,
    totalOccurrences: data.totalOccurrences,
  }
}


function App() {
  const [replayKey, setReplayKey] = useState(0)
  const [animationVisible, setAnimationVisible] = useState(false)
  const [sharedVows, setSharedVows] = useState<SharedVow[]>([])
  const animationRef = useRef<HTMLDivElement>(null)
  const sharedVowsRef = useRef<HTMLDivElement>(null)

  const scrollSharedVows = (direction: -1 | 1) => {
    const track = sharedVowsRef.current
    if (!track) return
    const card = track.querySelector<HTMLElement>(".community-card")
    const distance = (card?.offsetWidth ?? track.clientWidth * .85) + 18
    track.scrollBy({ left: direction * distance, behavior: "smooth" })
  }


  useEffect(() => {
    const target = animationRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimationVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.25 },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!firebaseApp) return
    const approved = query(
      collection(getFirestore(firebaseApp), 'sharedVows'),
      where('display', '==', true),
    )
    return onSnapshot(
      approved,
      (snapshot) => setSharedVows(snapshot.docs.map(sharedVow).filter((vow): vow is SharedVow => vow !== null)),
      () => setSharedVows([]),
    )
  }, [])

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="logo-link" href="#top" aria-label="VowCanvas home">
          <Logo />
        </a>
        <nav className="site-nav" aria-label="Primary navigation">
          <a href="#how-it-works">How it works</a>
          {sharedVows.length > 0 && <a href="#stories">Shared Vows</a>}
          <a href="#download">Get the app</a>
        </nav>
        <a className="button button--small button--outline" href="#download">Install VowCanvas app</a>
      </header>

      <main>
        <section className="hero section" id="top">
          <div className="hero__copy">
            <p className="eyebrow">A promise, documented over time</p>
            <h1>Make a promise.<br /><em>Make it visible.</em></h1>
            <p className="hero__lead">
              Choose a meaningful picture. Add proof as you keep your promise. Over time, those real moments rebuild the picture—one piece at a time.
            </p>
            <div className="hero__actions">
              <a className="button button--primary" href="#download">Install VowCanvas app <ArrowIcon /></a>
              <a className="text-link" href="#how-it-works">See how it works</a>
              {sharedVows.length > 0 && <a className="text-link" href="#stories">Explore shared Vows</a>}
            </div>
            <p className="hero__note"><span aria-hidden="true">✦</span> Missed days are part of the story, too.</p>
          </div>

          <div className="hero__visual" aria-label="A VowCanvas portrait assembled from proof photos">
            <span className="hero__wash hero__wash--one" aria-hidden="true" />
            <span className="hero__wash hero__wash--two" aria-hidden="true" />
            <div className="hero-art">
              <div className="hero-art__topline">
                <span>52 Sundays</span>
                <span>94 moments</span>
              </div>
              <MosaicAssembly compact />
              <div className="hero-art__caption">
                <span className="hero-art__check" aria-hidden="true">✓</span>
                <div><strong>You kept your promise.</strong><small>Visit Dad every Sunday</small></div>
              </div>
            </div>
            <div className="proof-chip proof-chip--one"><img src="/images/dad-proof-coffee.jpg" alt="" /><span>Sunday 12<small>Coffee and stories</small></span></div>
            <div className="proof-chip proof-chip--two"><img src="/images/dad-proof-album.jpg" alt="" /><span>Sunday 31<small>Family albums</small></span></div>
          </div>
        </section>

        <section className="belief-strip" aria-label="The VowCanvas idea">
          <p>Not a streak. Not a perfect record.</p>
          <p>A truthful picture of a promise you chose to keep.</p>
        </section>

        <section className="featured-vow section" aria-labelledby="featured-vow-title">
          <div className="featured-vow__image">
            <img src="/images/sundays-with-dad.jpg" alt="A father and son spending quiet time together" />
            <div className="featured-vow__progress">
              <span>18 of 52 Sundays</span>
              <div aria-hidden="true"><i /></div>
            </div>
          </div>
          <div className="featured-vow__copy">
            <p className="eyebrow">A Vow worth remembering</p>
            <h2 id="featured-vow-title">Visit Dad<br /><em>every Sunday.</em></h2>
            <p className="featured-vow__lead">Not because every Sunday will be perfect. Because one day, having been there will mean everything.</p>
            <blockquote>“I want the finished picture to hold the ordinary afternoons—the coffee, the stories, and the time we chose not to miss.”</blockquote>
            <div className="featured-vow__meta">
              <div><strong>1 year</strong><span>The promise</span></div>
              <div><strong>52 Sundays</strong><span>The rhythm</span></div>
              <div><strong>31 photos</strong><span>So far</span></div>
            </div>
            <a className="text-link" href="#how-it-works">See how a Vow unfolds</a>
          </div>
        </section>

        <section className="supporting-vow section" aria-labelledby="supporting-vow-title">
          <div className="supporting-vow__copy">
            <p className="eyebrow">Another reason to begin</p>
            <h2 id="supporting-vow-title">Go to the gym every day<br /><em>for one month.</em></h2>
            <p className="supporting-vow__lead">Not for a perfect body. To build the strength and energy for more of the life you want to carry.</p>
            <blockquote>“The picture I chose reminds me why I wanted to feel stronger in the first place.”</blockquote>
            <div className="featured-vow__meta">
              <div><strong>30 days</strong><span>The promise</span></div>
              <div><strong>Daily</strong><span>The rhythm</span></div>
              <div><strong>Up to 90</strong><span>Proof photos</span></div>
            </div>
          </div>
          <div className="supporting-vow__art">
            <figure>
              <img src="/images/gym-target.jpg" alt="A father carrying his daughter on a family hike" />
              <figcaption>The meaningful picture</figcaption>
            </figure>
            <figure>
              <img src="/images/gym-mosaic.jpg" alt="The completed gym Vow photo mosaic" />
              <figcaption>30 days made visible</figcaption>
            </figure>
          </div>
        </section>

        <section className="steps section" id="how-it-works">
          <div className="section-heading section-heading--center">
            <p className="eyebrow">How it works</p>
            <h2>Your promise becomes<br /><em>something you can see.</em></h2>
            <p>Three simple steps. One picture that could only be yours.</p>
          </div>

          <div className="stepper">
            <article className="step-card">
              <div className="step-card__number">01</div>
              <div className="step-card__visual step-card__visual--vow">
                <img src="/images/sundays-with-dad.jpg" alt="The meaningful father-and-son picture chosen for the Vow" />
                <span className="step-card__label">Your meaningful picture</span>
              </div>
              <h3>Make a Vow</h3>
              <p>Name what matters, choose the rhythm and duration, then pick the picture you want your effort to reveal.</p>
            </article>

            <article className="step-card">
              <div className="step-card__number">02</div>
              <div className="step-card__visual proof-stack" aria-label="Several proof photos added over time">
                <img src="/images/dad-proof-coffee.jpg" alt="Coffee together during a Sunday visit" />
                <img src="/images/dad-proof-album.jpg" alt="Looking through family photographs together" />
                <img src="/images/dad-proof-model-boat.jpg" alt="Building a small wooden boat together during a Sunday visit" />
                <span className="proof-stack__count">+3 today</span>
              </div>
              <h3>Add real proof</h3>
              <p>Whenever you follow through, keep up to three photos from that day. They become part of the final artwork.</p>
            </article>

            <article className="step-card">
              <div className="step-card__number">03</div>
              <div className="step-card__visual reveal-card">
                <img src="/images/dad-mosaic.jpg" alt="The completed Visit Dad every Sunday photo mosaic" />
                <span className="reveal-card__seal">✓</span>
              </div>
              <h3>Reveal your picture</h3>
              <p>Kept and missed days leave an honest mark. When the Vow ends, your finished picture preserves what really happened.</p>
            </article>
          </div>
        </section>

        <section className="reveal-section" ref={animationRef}>
          <div className="reveal-section__inner section">
            <div className="reveal-section__copy">
              <p className="eyebrow eyebrow--light">Made from the days you lived</p>
              <h2>A year of moments.<br /><em>One lasting picture.</em></h2>
              <p>Each small photo travels into its place. Together, they reveal the image you chose when the promise began.</p>
              <div className="reveal-stat-row" aria-label="Example Vow statistics">
                <div><strong>52</strong><span>Sundays</span></div>
                <div><strong>94</strong><span>proof photos</span></div>
                <div><strong>1</strong><span>story made visible</span></div>
              </div>
              <button className="button button--light" type="button" onClick={() => setReplayKey((value) => value + 1)}>
                Replay the reveal <span aria-hidden="true">↻</span>
              </button>
            </div>
            <div className={`reveal-stage ${animationVisible ? 'reveal-stage--visible' : ''}`}>
              {animationVisible ? <MosaicAssembly replayKey={replayKey} /> : <div className="reveal-placeholder" />}
              <div className="reveal-stage__caption"><span>Vow complete</span><strong>Visit Dad every Sunday</strong></div>
            </div>
          </div>
        </section>

        {sharedVows.length > 0 && (
          <section className="community section" id="stories">
            <div className="section-heading section-heading--split">
              <div><p className="eyebrow">Shared Vows</p><h2>Promises made<br /><em>visible together.</em></h2></div>
              <p>Some people choose to share their finished Vows. Not for applause—for connection, encouragement, and memory.</p>
            </div>

            <div className="community-carousel__toolbar">
              <span>{sharedVows.length} Vow{sharedVows.length === 1 ? "" : "s"} shared</span>
              <div className="community-carousel__controls" aria-label="Shared Vow carousel controls">
                <button type="button" onClick={() => scrollSharedVows(-1)} aria-label="Previous shared Vow">←</button>
                <button type="button" onClick={() => scrollSharedVows(1)} aria-label="Next shared Vow">→</button>
              </div>
            </div>
            <div className="community-grid" ref={sharedVowsRef} role="region" aria-label="Shared Vows carousel" tabIndex={0}>
              {sharedVows.map((vow) => {
                const progress = Math.round((vow.completedOccurrences / Math.max(vow.totalOccurrences, 1)) * 100)
                return (
                  <article className="community-card" key={vow.id}>
                    <div className="community-card__image">
                      <img src={vow.image} alt={`Photo mosaic for ${vow.title}`} />
                      <span className="status status--complete">Completed</span>
                    </div>
                    <div className="community-card__body">
                      <p className="community-card__kicker">Shared anonymously with VowCanvas</p>
                      <h3>{vow.title}</h3>
                      <div className="progress-track" aria-label={`${progress}% complete`}><span style={{ width: `${progress}%` }} /></div>
                      <div className="community-card__stats">
                        <span>{vow.completedOccurrences} of {vow.totalOccurrences} check-ins</span>
                        <span>{progress}% complete</span>
                        <span>1 Vow kept</span>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          </section>
        )}

        <section className="download section" id="download">
          <div className="download__art" aria-hidden="true">
            <div className="download__frame download__frame--back"><img src="/images/gym-target.jpg" alt="" /></div>
            <div className="download__phone"><img src="/images/dad-progress.jpg" alt="" /><span>18 of 52 Sundays</span></div>
            <div className="download__frame download__frame--front"><img src="/images/dad-mosaic.jpg" alt="" /></div>
          </div>
          <div className="download__copy">
            <p className="eyebrow">Install VowCanvas app</p>
            <h2>Keep what matters<br /><em>close at hand.</em></h2>
            <p>VowCanvas is now available for iPhone and Android. Make your Vow, keep the moments, and carry the unfolding picture with you.</p>
            <div className="store-buttons">
              <StoreButton platform="Android" url={androidUrl} />
              <StoreButton platform="iOS" url={iosUrl} />
            </div>
          </div>
        </section>

        <section className="contact section" id="contact">
          <img className="contact__mark" src="/brand/vowcanvas-mark.svg" alt="" aria-hidden="true" />
          <p className="eyebrow">Say hello</p>
          <h2>A promise starts<br />with a conversation.</h2>
          <p>Questions, press, partnerships, or simply a story you want to share—we would love to hear from you.</p>
          <a className="button button--primary" href={`mailto:${contactEmail}`}>Contact us <ArrowIcon /></a>
          <a className="contact__email" href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer__top">
          <Logo reversed />
          <p>Make a promise.<br />Make it visible.</p>
          <nav aria-label="Footer navigation">
            <a href="#how-it-works">How it works</a>
            {sharedVows.length > 0 && <a href="#stories">Shared Vows</a>}
            <a href="#download">Get the app</a>
            <a href="#contact">Contact</a>
          </nav>
        </div>
        <div className="site-footer__bottom">
          <span>© {new Date().getFullYear()} Halyard LLC. All rights reserved.</span>
          <span className="site-footer__legal"><a href="/privacy/">Privacy</a><a href="/terms/">Terms</a></span>
        </div>
      </footer>
    </div>
  )
}

export default App
